import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IRequirementResponse } from "@miplatform/types";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePermissions, useRequirements } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { SectionInfo } from "../../components/SectionInfo/SectionInfo";

export function Requirements() {
  const [selectedRequirement, setSelectedRequirement] =
    useState<IRequirementResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { navigate } = useRouter();

  const canUpdateRequirement = hasPermission(PERMISSIONS.REQUIREMENT.UPDATE);
  const canDeleteRequirement = hasPermission(PERMISSIONS.REQUIREMENT.DELETE);

  const {
    requirements,
    recentRequirements,
    refresh,
    isLoading,
    getRequirements,
    getRecentRequirements,
    deleteRequirement,
  } = useRequirements();

  const sections = [
    {
      fieldset: [
        {
          renderValue: (data: IRequirementResponse) => (
            <SectionInfo data={data} />
          ),
        },
      ],
    },
  ];

  const handleNavigate = (item: IRequirementResponse) => {
    if (item.id) {
      navigate(`/estimating/configuration/requirements/${item.id}`);
    }
  };

  const renderItemActions = (item: IRequirementResponse) => {
    const actions = [];

    if (canUpdateRequirement) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item),
      });
    }

    if (canDeleteRequirement) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }

    return actions.length > 0 ? (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    ) : null;
  };

  const handleOpenDeleteModal = (item: IRequirementResponse) => {
    setSelectedRequirement(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedRequirement) {
      deleteRequirement(selectedRequirement);
      setOpenDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Requirements"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          <MiButton
            title="Add Requirement"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() =>
              navigate("/estimating/configuration/add-requirements")
            }
            data-testid="add-requirement-btn"
          />
        }
        actionContainerStyle={{ flexWrap: "nowrap" }}
      />
      <MiBox>
        <MiList
          viewType="card"
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleNavigate,
            leftCardProps: { fieldset: [] },
          }}
          loading={isLoading}
          data={requirements}
          dataProvider={getRequirements}
          recentData={recentRequirements}
          recentDataProvider={getRecentRequirements}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          emptyListProps={{ text: "No Requirements found" }}
          data-testid="requirements-list"
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={openDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setOpenDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedRequirement?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
